import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Copy,
  Check,
  Mail,
  MapPin,
  Clock,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  CONTACT,
  SERVICE_OPTIONS,
  BUDGET_OPTIONS,
  TIMELINE_OPTIONS,
} from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useSastTime = () => {
  const [time, setTime] = useState("");
  useEffect(() => {
    const tick = () => {
      const t = new Intl.DateTimeFormat("en-GB", {
        timeZone: CONTACT.timezone,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(new Date());
      setTime(t);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
};

const CopyRow = ({ icon: Icon, label, value, href, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied to clipboard", { description: value });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return (
    <div
      data-testid={`contact-row-${testid}`}
      className="group flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-4 transition-all duration-300 hover:border-gray-300 hover:shadow-sm"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-neutral-100 text-black">
        <Icon size={18} />
      </span>
      <a href={href} className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-widest text-neutral-400">{label}</p>
        <p className="truncate font-medium text-black">{value}</p>
      </a>
      <button
        data-testid={`copy-${testid}`}
        onClick={copy}
        aria-label={`Copy ${label}`}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-neutral-500 transition-all duration-300 hover:border-black hover:text-black"
      >
        {copied ? <Check size={16} className="text-black" /> : <Copy size={16} />}
      </button>
    </div>
  );
};

const fieldClass =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-black placeholder:text-neutral-400 outline-none transition-all duration-300 focus:border-black focus:shadow-[0_0_0_3px_rgba(10,10,10,0.08)]";
const labelClass = "mb-2 block text-xs uppercase tracking-widest text-neutral-500";

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  service: SERVICE_OPTIONS[0],
  budget: BUDGET_OPTIONS[0],
  timeline: TIMELINE_OPTIONS[0],
  details: "",
};

const Contact = () => {
  const time = useSastTime();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.details) {
      toast.error("Please complete the required fields");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Inquiry received", {
        description: "Thank you. The A Flat Records team will be in touch shortly.",
      });
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white py-24 sm:py-32 text-black border-t border-gray-200"
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 max-w-2xl"
        >
          <p className="mb-3 text-sm font-medium tracking-widest text-neutral-400">
            GET IN TOUCH
          </p>
          <h2 className="font-heading text-4xl font-semibold tracking-tight sm:text-5xl">
            Let's build the next record.
          </h2>
          <p className="mt-4 text-neutral-600">
            For production, executive work, scoring and consultation. Reach out directly or
            send a project brief below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Info hub */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="rounded-3xl border border-gray-200 bg-neutral-50 p-6 sm:p-8">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-heading text-lg font-semibold tracking-tight">
                  Direct lines
                </h3>
                <span
                  data-testid="sast-time-badge"
                  className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700"
                >
                  <Clock size={13} />
                  <span className="tabular-nums">{time}</span> SAST
                </span>
              </div>

              <div className="space-y-3">
                <CopyRow
                  icon={Mail}
                  label="Direct email"
                  value={CONTACT.directEmail}
                  href={`mailto:${CONTACT.directEmail}`}
                  testid="direct-email"
                />
                <CopyRow
                  icon={Mail}
                  label="Bookings & inquiries"
                  value={CONTACT.bookingEmail}
                  href={`mailto:${CONTACT.bookingEmail}`}
                  testid="booking-email"
                />
              </div>

              <div className="mt-6">
                <div className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 text-neutral-600">
                  <MapPin size={16} className="text-black" /> {CONTACT.location}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Inquiry form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={submit}
              noValidate
              data-testid="inquiry-form"
              className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8"
            >
              <h3 className="mb-6 font-heading text-lg font-semibold tracking-tight">
                Project inquiry
              </h3>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Full name *</label>
                  <input
                    data-testid="input-full-name"
                    className={fieldClass}
                    value={form.full_name}
                    onChange={set("full_name")}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Email address *</label>
                  <input
                    data-testid="input-email"
                    type="email"
                    className={fieldClass}
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@label.com"
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Phone number</label>
                  <input
                    data-testid="input-phone"
                    className={fieldClass}
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className={labelClass}>Service required</label>
                  <select
                    data-testid="select-service"
                    className={fieldClass}
                    value={form.service}
                    onChange={set("service")}
                  >
                    {SERVICE_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label className={labelClass}>Release type</label>
                <select
                  data-testid="select-release-type"
                  className={fieldClass}
                  value={form.budget}
                  onChange={set("budget")}
                >
                  {BUDGET_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-5">
                <label className={labelClass}>Project timeline</label>
                <select
                  data-testid="select-timeline"
                  className={fieldClass}
                  value={form.timeline}
                  onChange={set("timeline")}
                >
                  {TIMELINE_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-5">
                <label className={labelClass}>Project details / brief *</label>
                <textarea
                  data-testid="input-details"
                  className={`${fieldClass} min-h-[130px] resize-y`}
                  value={form.details}
                  onChange={set("details")}
                  placeholder="Tell us about the project, artists involved, deliverables and goals."
                  required
                />
              </div>

              <button
                type="submit"
                data-testid="submit-inquiry"
                disabled={loading}
                className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-black px-6 py-4 font-medium text-white transition-all duration-300 hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send inquiry
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
